import React, { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import isPermissionEnabled from "common/isPermissionEnabled"
import MobileItemCardPreview from "./MobileItemCardPreview"
import WebItemCardPreview from "./WebItemCardPreview"
import { useItemPreviewCardData } from "./useItemPreviewCardData"
import {
  ITEM_PREVIEW_MENUS_CATEGORY_ID,
  MOBILE_ITEM_PREVIEW_PERMISSION_ID,
  WEB_ITEM_PREVIEW_PERMISSION_ID,
} from "./previewPermissionIds"
import styles from "./ItemPreviewField.module.css"

const TAB_MOBILE = "mobile"
const TAB_WEB = "web"

/**
 * Tabbed Mobile / Web item preview (permission-gated per tab).
 */
const ItemPreviewTabsField = ({
  values,
  selectedTags = [],
  mode = "add",
}) => {
  const { t } = useTranslation()
  const { userRole, accountType } = useSelector(state => state.Login)

  const canMobile = isPermissionEnabled(
    userRole,
    ITEM_PREVIEW_MENUS_CATEGORY_ID,
    MOBILE_ITEM_PREVIEW_PERMISSION_ID,
    accountType
  )
  const canWeb = isPermissionEnabled(
    userRole,
    ITEM_PREVIEW_MENUS_CATEGORY_ID,
    WEB_ITEM_PREVIEW_PERMISSION_ID,
    accountType
  )

  const tabs = useMemo(() => {
    const next = []
    if (canMobile) {
      next.push({ id: TAB_MOBILE, label: t("Mobile preview") })
    }
    if (canWeb) {
      next.push({ id: TAB_WEB, label: t("Web preview") })
    }
    return next
  }, [canMobile, canWeb, t])

  const [activeTab, setActiveTab] = useState(
    () => tabs[0]?.id || TAB_MOBILE
  )

  useEffect(() => {
    if (!tabs.some(tab => tab.id === activeTab)) {
      setActiveTab(tabs[0]?.id || TAB_MOBILE)
    }
  }, [tabs, activeTab])

  const enabled = canMobile || canWeb
  const {
    item,
    themeStyle,
    fastMovingIconUrl,
    fastMovingSettingsEnabled,
    fallbackImage,
  } = useItemPreviewCardData({
    values,
    selectedTags,
    mode,
    enabled,
  })

  if (!enabled || tabs.length === 0) return null

  return (
    <div className={`col-lg-6 ${styles.field}`} style={themeStyle}>
      {tabs.length > 1 ? (
        <div className={styles.tabs} role="tablist" aria-label={t("Item preview")}>
          {tabs.map(tab => {
            const isActive = activeTab === tab.id
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={isActive ? styles.tabActive : styles.tab}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            )
          })}
        </div>
      ) : (
        <span className={styles.label}>{tabs[0].label}</span>
      )}

      <div className={styles.previewStage} role="tabpanel">
        {activeTab === TAB_MOBILE && canMobile && (
          <MobileItemCardPreview
            item={item}
            fallbackImage={fallbackImage}
            fastMovingIconUrl={fastMovingIconUrl}
            fastMovingSettingsEnabled={fastMovingSettingsEnabled}
          />
        )}
        {activeTab === TAB_WEB && canWeb && (
          <WebItemCardPreview
            item={item}
            fallbackImage={fallbackImage}
            fastMovingIconUrl={fastMovingIconUrl}
            fastMovingSettingsEnabled={fastMovingSettingsEnabled}
          />
        )}
      </div>
    </div>
  )
}

export default ItemPreviewTabsField
